import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Link } from 'react-router-dom';

const AttorneyCard = ({ attorney }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('overview');

	const openModal = () => {
		setIsModalOpen(true);
		document.body.style.overflow = 'hidden';
	};

	const closeModal = () => {
		setIsModalOpen(false);
		document.body.style.overflow = 'unset';
	};

	const tabs = [{ id: 'overview', label: 'Overview', icon: 'User' }];

	return (
		<>
			<motion.div
				whileHover={{ scale: 1.02 }}
				whileTap={{ scale: 0.98 }}
				className='bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border border-gray-100'
				onClick={openModal}
			>
				<div className='relative'>
					<div className='aspect-[4/5] overflow-hidden'>
						<Image
							src={attorney?.image}
							alt={attorney?.name}
							className='w-full h-full object-cover transition-transform duration-300 hover:scale-105'
						/>
					</div>
					{attorney?.isPartner && (
						<div className='absolute top-4 right-4 bg-[#DB954E] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg'>
							Partner
						</div>
					)}
				</div>
				<div className='p-6'>
					<div className='mb-4'>
						<h3 className='font-playfair font-bold text-xl text-[#800020] mb-1'>
							{attorney?.name}
						</h3>
						<p className='text-[#DB954E] font-source text-sm font-semibold mb-2'>
							{attorney?.title}
						</p>
					</div>

					<div className='flex items-center justify-between pt-4 border-t border-gray-100'>
						<Button
							variant='outline'
							size='sm'
							className='border-[#800020] text-[#800020] hover:bg-[#800020] hover:text-white'
							iconName='ArrowRight'
							iconPosition='right'
							iconSize={14}
						>
							View Profile
						</Button>
					</div>
				</div>
			</motion.div>

			{/* Attorney Detail Modal */}
			<AnimatePresence>
				{isModalOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className='fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4'
						onClick={closeModal}
					>
						<motion.div
							initial={{ scale: 0.95, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.95, opacity: 0 }}
							transition={{ duration: 0.3 }}
							className='bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto shadow-2xl'
							onClick={(e) => e.stopPropagation()}
						>
							{/* Modal Header */}
							<div className='relative bg-gradient-to-r from-[#800020] to-[#800020]/90 p-6 rounded-t-2xl'>
								<button
									onClick={closeModal}
									className='absolute top-4 right-4 p-2 bg-white/90 hover:bg-white rounded-full transition-colors duration-200'
								>
									<Icon name='X' size={24} className='text-gray-600' />
								</button>
								<div className='text-white'>
									<h2 className='font-playfair font-bold text-3xl mb-1'>{attorney?.name}</h2>
									<p className='font-source text-lg text-[#DB954E] font-semibold'>
										{attorney?.title}
									</p>
								</div>
							</div>

							{/* Modal Content */}
							<div className='p-8'>
								<div className='grid lg:grid-cols-2 gap-8'>
									{/* Left Column - Image */}
									<div className='space-y-6'>
										<div className='relative'>
											<div className='aspect-[4/5] overflow-hidden rounded-xl'>
												<Image
													src={attorney?.image}
													alt={attorney?.name}
													className='w-full h-full object-cover object-top'
												/>
											</div>
											{attorney?.isPartner && (
												<div className='absolute top-4 right-4 bg-[#DB954E] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg'>
													Partner
												</div>
											)}
										</div>
									</div>

									{/* Right Column - Bio and Tabs */}
									<div className='space-y-6'>
										{/* Biography */}
										<div>
											<p className='font-source text-gray-700 leading-relaxed'>{attorney?.bio}</p>
										</div>

										{/* Tabs */}
										<div>
											{/* Tab Content */}
											<div className='min-h-[300px]'>
												<div className='space-y-4'>
													<div>
														<h4 className='font-source font-bold text-lg text-[#800020] mb-2'>
															Education
														</h4>
														<div className='space-y-2'>
															{attorney?.education?.map((edu, index) => (
																<div key={index} className='flex items-center space-x-3'>
																	<div className='w-10 h-10 bg-[#800020] rounded-lg flex items-center justify-center'>
																		<Icon name='GraduationCap' size={20} color='white' />
																	</div>
																	<p className='font-source text-gray-700'>{edu}</p>
																</div>
															))}
														</div>
													</div>
													{attorney?.professional_membership && (
														<div>
															<h4 className='font-source font-bold text-lg text-[#800020] mb-2'>
																Professional Membership
															</h4>
															<div className='flex items-center space-x-3'>
																<div className='w-10 h-10 bg-[#DB954E] rounded-lg flex items-center justify-center'>
																	<Icon name='Award' size={20} color='white' />
																</div>
																<p className='font-source text-gray-700'>
																	{attorney?.professional_membership}
																</p>
															</div>
														</div>
													)}
													<div>
														<h4 className='font-source font-bold text-lg text-[#800020] mb-2'>
															Practice Areas
														</h4>
														<div className='flex flex-wrap gap-2'>
															{attorney?.specializations?.map((spec, index) => (
																<span
																	key={index}
																	className='bg-[#800020]/10 text-[#800020] px-3 py-1 rounded-lg text-sm font-medium'
																>
																	{spec}
																</span>
															))}
														</div>
													</div>
													<div>
														<h4 className='font-source font-bold text-lg text-[#800020] mb-2'>
															Languages
														</h4>
														<div className='flex flex-wrap gap-2'>
															{attorney?.languages?.map((lang, index) => (
																<span
																	key={index}
																	className='bg-[#DB954E]/10 text-[#DB954E] px-3 py-1 rounded-lg text-sm font-medium'
																>
																	{lang}
																</span>
															))}
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Action Buttons */}
										<div className='space-y-4'>
											<Link to='/publications'>
												<Button
													variant='outline'
													className='w-full border-2 border-[#DB954E] text-[#DB954E] hover:bg-[#DB954E] hover:text-white font-source font-bold px-6 py-3'
													iconName='FileText'
													iconPosition='left'
													iconSize={18}
												>
													View Publications & Research
												</Button>
											</Link>
										</div>
									</div>
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};

export default AttorneyCard;
